import React, { useMemo } from 'react';
import { RingLoader } from 'react-spinners';
import { SectionHeader } from '../../design-system';
import FadeIn from './FadeIn';
import LiveNetworkStatus from './LiveNetworkStatus';
import LiveNetworkMap from './LiveNetworkMap';
import NetworkGalleryGrid from './NetworkGalleryGrid';
import ProofPhotoWall from './ProofPhotoWall';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

export default function LiveNetworkSection({
  compact = false,
  showStatusLink = true,
  className = '',
}) {
  const { screens, loading, error, usingFallback } = useNetworkStatus();

  const mappedScreens = useMemo(
    () => screens.filter((s) => s.latitude != null && s.longitude != null),
    [screens]
  );

  const hasMappedScreens = mappedScreens.length > 0;
  const hasLiveData = screens.length > 0 && !usingFallback;
  const hasGalleryPhotos = screens.some((s) => s.listingPhotoUrl);
  const hasNothing = !loading && screens.length === 0 && !error;

  const subtitle = hasMappedScreens
    ? hasLiveData
      ? 'Every pin is a real INVERT screen location.'
      : 'Real venues on the INVERT network.'
    : hasGalleryPhotos
      ? 'Real venues on the INVERT network.'
      : 'Screens across Los Angeles.';

  return (
    <div className={className}>
      <SectionHeader
        label={hasLiveData ? 'Live Network' : 'Network'}
        title={hasLiveData ? 'Live INVERT Network' : 'INVERT Network'}
        subtitle={subtitle}
      />

      {loading && (
        <div className="flex justify-center py-16">
          <RingLoader color="#dc5024" loading size={40} aria-label="Loading network" />
        </div>
      )}

      {!loading && hasMappedScreens && (
        <div className={`grid ${compact ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-8`}>
          {!compact && (
            <FadeIn>
              <LiveNetworkStatus compact={compact} showLink={showStatusLink} />
            </FadeIn>
          )}
          <FadeIn delay={compact ? 0 : 0.1}>
            <LiveNetworkMap showLiveStatus={hasLiveData} />
          </FadeIn>
          {compact && (
            <FadeIn delay={0.1}>
              <LiveNetworkStatus compact showLink={showStatusLink} />
            </FadeIn>
          )}
        </div>
      )}

      {!loading && !hasMappedScreens && hasGalleryPhotos && (
        <FadeIn>
          <NetworkGalleryGrid screens={screens} linkToNetwork={!compact} />
        </FadeIn>
      )}

      {!loading && !hasMappedScreens && !hasGalleryPhotos && (
        <FadeIn>
          <div className={compact ? 'space-y-6' : 'grid lg:grid-cols-2 gap-8'}>
            <LiveNetworkMap showLiveStatus={false} />
            {!compact && (
              <div className="flex items-center">
                <ProofPhotoWall />
              </div>
            )}
          </div>
          {compact && (
            <div className="mt-6">
              <ProofPhotoWall />
            </div>
          )}
        </FadeIn>
      )}

      {!loading && hasNothing && (
        <FadeIn>
          <ProofPhotoWall />
        </FadeIn>
      )}
    </div>
  );
}
